<template>
  <HeaderBar />

  <section class="carvia-container">
    <Tabs v-model="activeTab" :tabs="tabs.filter((tab) => tab.available)" class="mb-4 mt-6" />

    <div class="space-y-6">
      <PaginatedList :load="load" :query-key="[APPROVAL_REQUESTS_QUERY_KEY, query]">
        <template #entry="data">
          <ApprovalRequestItem
            :approval-request="data.entry"
            :is-assigned-to-me="activeTab === Tab.ASSIGNED_TO_ME"
          />
        </template>
      </PaginatedList>
    </div>
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { computed, ref, watchEffect } from 'vue';

import Tabs from '@/components/Tabs.vue';
import { useMe } from '@/queries/use-users';
import { Order } from '@/entities/pagination/order.enum';
import ApprovalRequestItem from './ApprovalRequestItem.vue';
import { useUiStore } from '@/stores/ui.store';
import {
  ApprovalRequestFilter,
  ApprovalRequestSort,
  ApprovalRequestStatus,
} from '@carvia/ros-client-types';
import { useAbility } from '@casl/vue';
import { approvalRequestsService } from '@/api/approval-requests.service';
import PaginatedList from '@/components/PaginatedList.vue';
import { APPROVAL_REQUESTS_QUERY_KEY } from '@/queries/use-approval-requests';

const load = async (page: number) => {
  return approvalRequestsService.getAllWithQuery({ ...query.value, page });
};

enum Tab {
  ASSIGNED_TO_ME = 'ASSIGNED_TO_ME',
  MY_REQUESTS = 'MY_REQUESTS',
}

const { t } = useI18n();
const uiStore = useUiStore();

const activeTab = ref(Tab.ASSIGNED_TO_ME);
const { data: me } = useMe();

const query = computed(() => {
  const baseQuery = {
    limit: 10,
    sort: [ApprovalRequestSort.CREATED_DATE],
    order: Order.DESC,
  };

  if (activeTab.value === Tab.MY_REQUESTS && me.value) {
    return {
      ...baseQuery,
      filter: ApprovalRequestFilter.CREATED_BY_ME,
    };
  }

  return {
    ...baseQuery,
    status: ApprovalRequestStatus.REQUESTED,
    filter: ApprovalRequestFilter.ASSIGNED_TO_ME,
  };
});

const { can } = useAbility();

const tabs = computed(() => [
  {
    title: t('assignedToMe'),
    id: Tab.ASSIGNED_TO_ME,
    available: can('process', 'ApprovalRequest'),
  },
  {
    title: t('myRequests'),
    id: Tab.MY_REQUESTS,
    available: can('view', 'ApprovalRequest'),
  },
]);

watchEffect(() => {
  uiStore.setHeaderTitle(t('approvalRequests'));
});
</script>

<i18n lang="json">
{
  "en": {
    "assignedToMe": "Assigned to me",
    "myRequests": "My requests",
    "approvalRequests": "Approval Requests"
  },
  "de": {
    "assignedToMe": "Mir zugewiesen",
    "myRequests": "Meine Anfragen",
    "approvalRequests": "Genehmigungsanfragen"
  },
  "es": {
    "assignedToMe": "Asignadas a mí",
    "myRequests": "Mis solicitudes",
    "approvalRequests": "Solicitudes de aprobación"
  }
}
</i18n>
